import { bytesToBase64, MAX_BACKGROUND_BYTES, type ThemeImage } from "../../domain/themeFile";

/** The long side a background is resized to. A full-HD screen shows it sharp; a phone downloads far less than the camera original. */
export const MAX_BACKGROUND_SIDE = 1920;

const QUALITIES = [0.85, 0.75, 0.65, 0.55];

/**
 * A picture the operator chose, made into a background radhotspotapi accepts:
 * at most 1920 px on its long side, a JPEG of at most 600 KB. The browser does
 * the work, so a 6 MB phone photo never leaves the operator's machine as is.
 *
 * Quality steps down first, then the size, until it fits. The server checks the
 * bytes again; this only spares the operator a refusal.
 */
export async function readBackground(file: File): Promise<ThemeImage> {
  if (file.type && !file.type.startsWith("image/")) throw new Error("Choose a picture: a JPEG, PNG or WebP file.");
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error("That file could not be read as a picture.");
  }
  try {
    let scale = Math.min(1, MAX_BACKGROUND_SIDE / Math.max(bitmap.width, bitmap.height));
    for (let round = 0; round < 6; round++) {
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("This browser cannot resize pictures.");
      // A transparent PNG becomes JPEG: paint white under it rather than black.
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      for (const q of QUALITIES) {
        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", q));
        if (blob && blob.size <= MAX_BACKGROUND_BYTES) {
          return { type: "image/jpeg", base64: bytesToBase64(new Uint8Array(await blob.arrayBuffer())) };
        }
      }
      scale *= 0.8;
    }
    throw new Error("That picture could not be made smaller than 600 KB. Try another one.");
  } finally {
    bitmap.close();
  }
}
